import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { store } from 'redux-rest-reducer';
import moment from 'moment';
import styled, { keyframes } from 'styled-components';
import { Col, Row, Text, Button } from '@cuemath/comps';
import { Call } from 'styled-icons/material/Call';
import { Block } from 'styled-icons/boxicons-regular/Block';

import {
  patch as patchTeacherReview,
  get as getClassLogs,
  post as postTeacherReview,
  invalidateItem as invalidateReview,
} from 'actions/teacher-performance-review';
import { openModal, closeModal } from 'actions/modal';
import { get as getConfig } from 'actions/configs';
import { patch as patchTeacherClassroom } from 'actions/teacher-classrooms';
import { get as getTeacherClassStatus } from 'actions/teacher-class-status';

import { TEACHER_PERFORMANCE_REVIEW } from 'constants/api';
import { MODAL_FORM } from 'constants/form';
import {
  SHOW_STATE,
  STATE,
  buttonActions,
  AVERAGE,
  REVIEW_TYPE,
} from 'constants/reviews';
import { LoaderAlt } from 'styled-icons/boxicons-regular/LoaderAlt';

import ClassroomForm from './view';
import COLORS from '../../constants/colors';

const DEMO_ACTION_ITEMS_COUNT = 3;

const ERROR = 'Something went wrong';

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const LoadingRow = styled(Row)`
  margin: 10px 0 0 30px;
`;

const LoaderIcon = styled(LoaderAlt)`
  margin: 2px;
  height: 20px;
  animation: ${spin} 1s linear infinite;
`;

const BlockIcon = styled(Block)`
  width: 20px;
  height: 20px;
  color: red;
  font-weight: bold;
`;

const LoadingText = styled(Text)`
  font-size: 14px;
  font-weight: bold;
  width: 580px;
  padding-left: 5px;
`;

const CallIcon = styled(Call)`
  width: 20px;
  height: 20px;
`;

const Header = styled.div`
  height: 100%;
  background: #ffffff;
  border-bottom: 1px solid #eceef7;
  border-top: 1px solid #eceef7;
  z-index: 13;
  display: flex;
  justify-content: space-between;
  padding-bottom: 10px;
`;

const InfoText = styled(Text)`
  margin: 10px;
  width: 250px;
  overflow-wrap: break-word;
`;

const StatusText = styled(Text)`
  position: relative;
  margin: 10px;
`;

const AlignRow = styled(Row)`
  flex-wrap: nowrap;
`;

class TeacherClassroom extends Component {
  static defaultProps = {};

  static propTypes = {
    actions: PropTypes.shape({
      openModal: PropTypes.func.isRequired,
      getClassLogs: PropTypes.func.isRequired,
      getTeacherClassStatus: PropTypes.func.isRequired,
      getConfig: PropTypes.func.isRequired,
      invalidateReview: PropTypes.func.isRequired,
    }).isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      remark: {},
      questionRes: {},
      averageRes: null,
      addedLogs: null,
      isPatchDone: null,
      isPublished: false,
      reviewStarted: false,
      reviewSubmitted: false,
      reviewClass: null,
      selectedClass: null,
      plusValues: {},
      teacherReviewPatch: store.patch(),
      reviewPost: store.post(),
      teacherActions: null,
      comments: null,
      errorMsg: null,
      demoActions: [],
      demoActionsError: '',
    };
  }

  static getDerivedStateFromProps(props, state) {
    const { teacherClassLogs, teacherClassroomId } = props;
    const { reviewPost, teacherReviewPatch } = state;
    let { errorMsg } = state;

    const callLogsGet = store.get(teacherClassLogs, teacherClassroomId);
    const reviewPostData = store.post(teacherClassLogs, reviewPost.id);

    const reviewPatchData = store.patch(teacherClassLogs, teacherReviewPatch.id);

    let patchData, postData;

    if (reviewPostData.resolved) {
      if (reviewPostData.resolved.isCreated) {
        postData = reviewPostData;
      } else if (reviewPostData.resolved.isCreatingFailed) {
        errorMsg = ERROR;
      }
    }

    if (reviewPatchData.resolved) {
      if (reviewPatchData.resolved.isPatched) {
        patchData = reviewPatchData;
      } else if (reviewPatchData.resolved.isPatchingFailed) {
        errorMsg = ERROR;
      }
    }

    return {
      callLogData: callLogsGet,
      patchData,
      postData,
      errorMsg,
    };
  }

  componentDidMount() {
    const { actions, teacherClassroomId } = this.props;

    if (teacherClassroomId) {
      actions.getClassLogs(teacherClassroomId);
      actions.getTeacherClassStatus(teacherClassroomId);
    }

    actions.getConfig(TEACHER_PERFORMANCE_REVIEW);
  }

  componentDidUpdate(prevProps) {
    const { reviewStarted, patchData, postData, isPatchDone } = this.state;

    if (postData && postData.resolved && postData.resolved.isCreated && !reviewStarted) {
      this.setState({ reviewStarted: true });

      this.handleInvalidateClassLogs();
    }

    if (patchData && patchData.resolved && patchData.resolved.isPatched && isPatchDone) {
      this.setState({ isPatchDone: null, reviewSubmitted: true });

      this.handleInvalidateClassLogs();
    }
  }

  handleInvalidateClassLogs = () => {
    const { actions, teacherClassroomId } = this.props;

    if (!teacherClassroomId) return null;

    if (teacherClassroomId) {
      actions.getClassLogs(teacherClassroomId);
    }

    return null;
  };

  handleOptionChange = (event, stateValues) => {
    const { name, value } = event.target;
    const { reviewSubmitted } = this.state;

    if (stateValues && stateValues[name]) {
      const questions = Object.assign({}, stateValues);

      questions[name] = {
        option: value,
        comment: stateValues[name].comment,
      };

      this.setState({ questionRes: questions });
    } else {
      const questions = Object.assign({}, this.state.questionRes);

      questions[name] = {
        option: value,
        comment: '',
      };

      this.setState({ questionRes: questions });
    }

    if (reviewSubmitted) {
      this.setState({ reviewSubmitted: false });
    }
  };

  handleAverageChange = (e, stateValue) => {
    const { value, checked } = e.target;

    let avgValue;
    if (value && checked) {
      avgValue = AVERAGE[value];
    } else {
      avgValue = AVERAGE.NONE;
    }

    this.setState({ averageRes: avgValue });
  };

  onMessageChange = (event, id) => {
    const comments = Object.assign({}, this.state.remark);

    comments[id] = event.target.value;

    this.setState({ remark: comments });
  };

  onReviewThisSelected = () => {
    const { actions } = this.props;
    const { callLogData } = this.state;

    actions.openModal(
      MODAL_FORM.REVIEW_TYPE_MODAL,
      {
        size: 'custom',
        width: '360px',
        height: 'fit-content',
      },
      {
        onReviewTypeSelected: this.onReviewTypeSelected,
        callLogData,
        onReviewUpdate: this.submitReview,
      },
    );
  };

  onReviewTypeSelected = selectedOption => {
    const { actions, teacherClassroomId } = this.props;
    const {
      callLogData,
      reviewPost: { id },
      teacherReviewPatch,
    } = this.state;
    const { data } = callLogData || {};
    const { class_data: classData, class_reviews: classReviews } = data || {};
    const { teacher_id: teacherId } = classData || {};

    const [ongoingObj] = classReviews || [];
    const { id: reviewId } = ongoingObj || {};

    const isOngoing = classReviews && Array.isArray(classReviews) && classReviews.length;
    this.setState({ reviewClass: true, selectedClass: selectedOption });

    let request;
    if (isOngoing) {
      request = {
        action: buttonActions.REVIEW_UPDATE,
        action_data: { review_type: selectedOption && selectedOption.id },
      };

      actions.patchTeacherReview(teacherReviewPatch.id, reviewId, request);

      this.setState({ isPatchDone: true });
    } else {
      request = {
        teacher_id: teacherId,
        teacher_classroom_id: teacherClassroomId,
        review_type: selectedOption && selectedOption.id,
      };

      actions.postTeacherReview(id, request);
    }

    actions.closeModal();
  };

  onCommentChange = e => {
    this.setState({ comments: e.target.value });
  };

  onActionChange = e => {
    this.setState({ teacherActions: e.target.value });
  };

  onDemoActionChange = (value, index) => {
    const demoActionsError = value.trim().length > 70 ? 'No more than 70 characters' : '';
    this.setState({ demoActionsError });
    if (!demoActionsError) {
      const demoActions = [...this.state.demoActions];
      demoActions[index] = value;
      this.setState({
        demoActions: demoActions,
      });
    }
  };

  onSubmitAction = type => {
    const { actions } = this.props;
    let confirmText = '';
    let headerText = '';

    switch (type) {
      case buttonActions.PUBLISH:
        confirmText = `The review feedback will be published to the teacher.`;
        headerText = `Are you sure you want to publish?`;
        actions.openModal(
          MODAL_FORM.REVIEW_CONFIRM_MODAL,
          {
            size: 'custom',
            width: `400px`,
            height: `170px`,
          },
          { onConfirm: this.submitReview, confirmText, headerText, actionType: type },
        );
        break;

      case buttonActions.UNPUBLISH:
        confirmText = `The review will be removed from the teacher interface.`;
        headerText = `Are you sure you want to unpublish?`;
        actions.openModal(
          MODAL_FORM.REVIEW_CONFIRM_MODAL,
          {
            size: 'custom',
            width: `400px`,
            height: `170px`,
          },
          { onConfirm: this.submitReview, confirmText, headerText, actionType: type },
        );
        break;

      case buttonActions.SAVE:
        this.submitReview(type);
        break;

      default:
        return;
    }
  };

  getResultValues = (result, newResult, key) => {
    let newArr, arr1;
    if (
      result &&
      newResult &&
      Array.isArray(newResult) &&
      Array.isArray(result) &&
      result.length &&
      newResult.length
    ) {
      arr1 = result.filter(a => !newResult.some(b => b[key] === a[key]));
      const finalArr = this.getRadioSubmit(arr1);

      newArr = [...finalArr, ...newResult];
    } else if (
      result &&
      newResult &&
      Array.isArray(result) &&
      Array.isArray(newResult) &&
      result.length &&
      !newResult.length
    ) {
      newArr = this.getRadioSubmit(result);
    }

    return newArr;
  };

  getRadioSubmit = result => {
    const { questionRes, remark } = this.state;

    let submitArr = [];

    if (result && result.length > 0) {
      result.forEach(k => {
        const optId = k && k.id ? k.id : k;
        let optionVal = '';
        let optComment = '';

        if (questionRes[optId]) {
          if (questionRes[optId].option) {
            optionVal = questionRes[optId].option;
            optComment = questionRes[optId].comment;
          } else {
            optionVal = questionRes[optId];
          }
        }

        if (remark && remark[optId]) {
          optComment = remark[optId];
        }

        submitArr.push({
          id: optId,
          option: !optionVal && k && k.option ? k.option : optionVal,
          score: 0,
          comments: !optComment && k && k.comments ? k.comments : optComment,
        });
      });
    }

    return submitArr;
  };

  submitReview = type => {
    const { actions, teacherClassLogs, teacherClassroomId } = this.props;

    const {
      teacherReviewPatch,
      remark,
      averageRes,
      questionRes,
      teacherActions,
      comments,
      callLogData,
      selectedClass,
      plusValues,
      demoActions,
    } = this.state;

    const { data } = callLogData || {};

    const { class_reviews: classReviews } = data || {};
    const ongoingData = this.getSelectedClass();

    let reviewData;
    if (selectedClass && selectedClass.id) {
      reviewData =
        classReviews && Array.isArray(classReviews) && classReviews.length
          ? classReviews.find(fb => fb.review_type === selectedClass.id)
          : [];
    } else {
      const [logs] = ongoingData || [];
      reviewData = logs;
    }

    if (!reviewData || !Object.keys(reviewData).length) {
      const getLogs = store.get(teacherClassLogs, teacherClassroomId);

      const { data: logsData } = getLogs || {};

      if (logsData && Object.keys(logsData).length > 0) {
        const { class_reviews: classReviewData } = logsData || {};

        const [logRes] = classReviewData || [];

        reviewData = logRes;
      }
    }

    const {
      id: reviewId,
      result,
      logs_reviewed: logsReviewed,
      action_points: savedDemoActions = [],
    } = reviewData || {};

    let patchData;
    const updatedDemoActions = this.getDemoActions(demoActions, savedDemoActions);

    switch (type) {
      case buttonActions.PUBLISH:
      case buttonActions.SAVE:
        const keys = questionRes && Object.keys(questionRes);

        const submitArr = this.getRadioSubmit(keys);

        let newRadioData;
        if (
          result &&
          Array.isArray(result) &&
          result.length &&
          ((submitArr && Array.isArray(submitArr) && submitArr.length) ||
            (remark && Object.keys(remark).length > 0))
        ) {
          newRadioData = this.getResultValues(result, submitArr, 'id');
        } else {
          newRadioData = submitArr;
        }
        const selectedLogs = logsReviewed && logsReviewed.length;

        let plusObj =
          plusValues && Object.keys(plusValues) > 0 && selectedLogs
            ? this.getLogValues(plusValues, logsReviewed)
            : plusValues;

        var logs = Object.keys(plusObj);
        let logArr = [];

        if (logs && logs.length) {
          logs.forEach(lg => {
            if (plusValues[lg]) {
              logArr.push(plusValues[lg]);
            }
          });
        }

        const obj = {};
        if (teacherActions) {
          obj.teacher_actions = teacherActions;
        }

        if (comments) {
          obj.comments = comments;
        }

        if (updatedDemoActions.join() !== savedDemoActions.join()) {
          const filteredDemoActions = updatedDemoActions.filter(demoAction => demoAction);

          obj.action_points = filteredDemoActions;
        }
        if (averageRes) {
          obj.review_action = !averageRes ? AVERAGE.NONE : averageRes;
        }

        if (newRadioData && newRadioData.length) {
          obj.result = newRadioData;
        }

        if (logArr && logArr.length) {
          obj.logs_reviewed = logArr;
        }

        patchData = {
          action: type,
          action_data: obj,
        };
        break;

      case buttonActions.UNPUBLISH:
        patchData = { action: type, action_data: {} };
        break;

      default:
        return;
    }

    if (!reviewId) {
      this.setState({ errorMsg: ERROR });
    }

    actions.patchTeacherReview(teacherReviewPatch.id, reviewId, patchData);

    if (type === buttonActions.PUBLISH) {
      this.setState({ isPublished: true });
    }

    this.setState({ isPatchDone: true });
  };

  getSelectedClass = () => {
    const { callLogData } = this.state;
    const { data } = callLogData || {};

    const { class_reviews: classReviews } = data || {};

    const ongoingData =
      classReviews && Array.isArray(classReviews) && classReviews.length
        ? classReviews.filter(res => res.review_status === STATE[res.review_status])
        : false;

    return ongoingData;
  };

  getLogValues = (plusObj, logsArray) => {
    const keys = Object.keys(plusObj);

    if (
      keys &&
      keys.length &&
      logsArray &&
      Array.isArray(logsArray) &&
      logsArray.length
    ) {
      keys.forEach(k => {
        const logIndex = logsArray
          .map(x => {
            if (x && x.log_id) {
              return x.log_id;
            }
            return null;
          })
          .indexOf(k);

        if (!plusObj[k] && keys.indexOf(k) > -1 && logIndex > -1) {
          logsArray.splice(logIndex, 1);
        }
      });
    }

    logsArray.forEach(log => {
      const logId = log && log.log_id;
      plusObj[logId] = log;
    });

    return plusObj;
  };

  getConfigData = selectedId => {
    const { teacherReviewConfig } = this.props;
    const { data } = teacherReviewConfig || {};

    const { feedback } = data || {};
    const mathFeedback =
      feedback && feedback.length && feedback.find(fb => fb.id === selectedId);

    const { form: feedbackForm } = mathFeedback || {};
    const [questionsData] = feedbackForm || [];

    const { questions } = questionsData || {};
    const radioData = questions;

    return radioData;
  };

  onPlusClicked = (id, data) => {
    const values = Object.assign({}, this.state.plusValues);

    values[id] = data;

    this.setState({ plusValues: values });
  };

  onLogClicked = (logUrl, callType, logType, totalLogsCount) => {
    const { actions, teacherClassroomId } = this.props;
    actions.patchTeacherClassroom(store.patch().id, teacherClassroomId, {
      action: 'class_log_clicked',
      action_data: {
        log_url: logUrl,
        call_type: callType,
        log_type: logType,
        total_logs_count: totalLogsCount,
      },
    });
  };

  padNum = num => {
    return ('0' + num).slice(-2);
  };

  getMinutes = totalSeconds => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds - hours * 3600 - minutes * 60;

    return `${this.padNum(hours)}:${this.padNum(minutes)}:${this.padNum(seconds)}`;
  };

  getRadioValues = result => {
    let response = {};

    if (result && Array.isArray(result) && result.length) {
      result.forEach(res => {
        response[res.id] = {
          option: res && res.option,
          comment: res && res.comments,
        };
      });
    }

    return response;
  };

  isNull = arr => {
    return arr.join().replace(/,/g, '').length === 0;
  };

  getDemoActions = (demoActions, savedDemoActions) => {
    const latestDemoActions = [];
    for (var i = 0; i < DEMO_ACTION_ITEMS_COUNT; i++) {
      latestDemoActions.push(
        demoActions[i] || demoActions[i] === '' ? demoActions[i] : savedDemoActions[i],
      );
    }
    return latestDemoActions;
  };

  onClickJoinClass = () => {
    const { teacherClassroomId } = this.props;
    window.open(`/student-demo-room/${teacherClassroomId}`);
  };

  renderClassDetail = (classSummery, reviewObj, isFormDisabled) => {
    const { teacherClassroomId } = this.props;

    const {
      class_data: classData,
      class_reviews: classReviews,
      reviewer,
      call_logs_duration: classLogsDuration,
    } = classSummery || {};

    const { review_status: reviewStatus, review_type: reviewType } = reviewObj || {};

    const {
      date,
      rm_call_enabled: rmCallEnabled,
      class_type: classType,
      teacher_name: teacherName,
      teacher_phone: teacherPhone,
      teacher_email: teacherEmail,
      scheduled_start_time: scheduledStartTime,
      scheduled_end_datetime: scheduledEndDatetime,
      exit_datetime: exitDatetime,
      trial_section: trialSection,
      offering_section_start_time: offeringSectionStartTime,
    } = classData || {};

    const currentDt = new Date().getTime() / 1000;
    const isClassNotLive = scheduledEndDatetime <= currentDt;

    const notShowClassJoin =
      isClassNotLive || !rmCallEnabled || exitDatetime || classType !== 'DEMO';

    const showEdit =
      classReviews &&
      Array.isArray(classReviews) &&
      classReviews.length &&
      !isFormDisabled;

    const time = moment.unix(scheduledStartTime).format('h:mm A');
    const totalDuration = classLogsDuration ? this.getMinutes(classLogsDuration) : '';
    const classDate = moment(date * 1000).format('DD-MM-YYYY');

    const joinSection = () => {
      if (!notShowClassJoin && rmCallEnabled) {
        return (
          <Button
            target="_blank"
            label="Join Class"
            id={`join-button:${teacherClassroomId}`}
            onClick={() => this.onClickJoinClass()}
            renderAs="inverted"
            icon={CallIcon}
          />
        );
      }
    };

    const classTypeText = rmCallEnabled ? `${classType} (Rm-Call)` : classType;

    return (
      <Header>
        <Col>
          <InfoText>Teacher Name: {teacherName}</InfoText>
          <InfoText>Teacher Phone: {teacherPhone} </InfoText>
          <StatusText>Teacher Email: {teacherEmail} </StatusText>
        </Col>
        <Col>
          <InfoText align="left">Date: {classDate}</InfoText>
          <InfoText>Time: {time}</InfoText>
          {rmCallEnabled && joinSection()}
        </Col>
        <Col>
          <InfoText>Total Call Duration: {totalDuration}</InfoText>
          <InfoText>Type of class: {classTypeText}</InfoText>
          {trialSection && <InfoText>Current Trial Section: {trialSection}</InfoText>}
          {offeringSectionStartTime && (
            <InfoText>Offering Section Started At: {offeringSectionStartTime}</InfoText>
          )}
        </Col>
        <Col>
          <div>
            <AlignRow>
              {reviewStatus && (
                <StatusText align="left">
                  Review Type: {REVIEW_TYPE[reviewType]}{' '}
                </StatusText>
              )}
              {showEdit ? (
                <Button
                  label="Edit"
                  renderAs="link"
                  onClick={this.onReviewThisSelected}
                />
              ) : (
                ''
              )}
            </AlignRow>
            <StatusText align="left">
              Status: {!reviewStatus ? 'Not Reviewed' : SHOW_STATE[reviewStatus]}
            </StatusText>
            {reviewStatus && <StatusText align="left">Reviewer: {reviewer}</StatusText>}
          </div>
        </Col>
      </Header>
    );
  };

  render() {
    const {
      selectedClass,
      callLogData,
      plusValues,
      remark,
      questionRes,
      averageRes,
      comments,
      teacherActions,
      reviewSubmitted,
      demoActions,
      demoActionsError,
      errorMsg,
    } = this.state;
    let { reviewClass } = this.state;
    const { location } = this.props;
    const { search = '' } = location || {};

    const {
      data: callData,
      isLoadingFailed: callDataLoadFailed,
      isLoading: callDataLoading,
    } = callLogData || {};

    if (callDataLoading) {
      return (
        <LoadingRow>
          <LoaderIcon color={COLORS.BOSTON_BLUE} />
          <LoadingText>Calling Data Loading...</LoadingText>
        </LoadingRow>
      );
    }

    if (callDataLoadFailed) {
      return (
        <LoadingRow>
          <BlockIcon />
          <LoadingText>Teacher classroom was marked deleted/skipped.</LoadingText>
        </LoadingRow>
      );
    }

    const {
      student_call_logs: classLogs,
      class_reviews: classReviews,
      broadcast_call_logs: broadcastLogs,
      group_call_logs: groupCallLogs,
    } = callData || {};

    const ongoingData = this.getSelectedClass();

    if (ongoingData && Array.isArray(ongoingData) && ongoingData.length) {
      reviewClass = true;
    }

    let resData = [];

    if (selectedClass && selectedClass.id) {
      resData =
        classReviews && Array.isArray(classReviews) && classReviews.length
          ? classReviews.filter(rs => rs.review_type === selectedClass.id)
          : [];
    } else {
      resData = ongoingData;
    }

    const [reviewObj] = resData || [];

    const {
      review_status: reviewStatus,
      review_action: reviewAction,
      comments: savedComments,
      logs_reviewed: logsReviewed,
      teacher_actions: selectedTeacherActions,
      result,
      review_type: reviewType,
      action_points: savedDemoActions = [],
    } = reviewObj || {};

    const selectedAvg = {
      s2_q1: {
        option: !averageRes ? reviewAction : averageRes,
        comment: '',
      },
    };

    const isFormDisabled =
      reviewStatus === STATE.PUBLISHED ||
      reviewStatus === STATE.ACKNOWLEDGED ||
      reviewStatus === STATE.UNPUBLISHED;

    let questionsData;

    if (selectedClass && selectedClass.id) {
      questionsData = this.getConfigData(selectedClass.id);
    } else {
      const [logs] = ongoingData || [];
      const { review_type: reviewType } = logs || {};
      questionsData = this.getConfigData(reviewType);
    }

    const isQuestionRes =
      questionRes &&
      Object.keys(questionRes).length > 0 &&
      questionRes.constructor === Object;

    return (
      <div>
        {this.renderClassDetail(callData, reviewObj, isFormDisabled)}
        <ClassroomForm
          broadcastCallLogData={broadcastLogs}
          classLogs={classLogs}
          demoActions={this.getDemoActions(demoActions, savedDemoActions)}
          demoActionsError={demoActionsError}
          errorMsg={errorMsg}
          getLogValues={this.getLogValues}
          getRadioValues={this.getRadioValues}
          groupCallLogData={groupCallLogs}
          handleAverageChange={this.handleAverageChange}
          handleOptionChange={this.handleOptionChange}
          isDemoReview={reviewType === 'MATH_DEMO' || reviewType === 'CODING_DEMO'}
          isFormDisabled={isFormDisabled}
          isQuestionRes={isQuestionRes}
          isSales={search === '?sales=True'}
          logsReviewed={logsReviewed}
          onActionChange={this.onActionChange}
          onCommentChange={this.onCommentChange}
          onDemoActionChange={this.onDemoActionChange}
          onMessageChange={this.onMessageChange}
          onLogClicked={this.onLogClicked}
          onPlusClicked={this.onPlusClicked}
          onReviewThisSelected={this.onReviewThisSelected}
          onSubmitAction={this.onSubmitAction}
          plusValues={plusValues}
          questionRes={questionRes}
          questionsData={questionsData}
          remark={remark}
          result={result}
          reviewClass={reviewClass}
          reviewStatus={reviewStatus}
          reviewSubmitted={reviewSubmitted}
          savedComments={!comments ? savedComments : comments}
          selectedAvg={selectedAvg}
          selectedTeacherActions={
            !teacherActions ? selectedTeacherActions : teacherActions
          }
        />
      </div>
    );
  }
}

export default connect(
  (
    { configs, teacherClassLogs, teacherClassStatus },
    { match: { params: { teacherClassroomId } } = {} },
  ) => {
    const teacherClassStatusData = store.get(
      teacherClassStatus,
      `${teacherClassroomId || ''}`,
    );

    const { data: classStatus } = teacherClassStatusData || {};

    const { teacher_id: teacherId = '' } = classStatus || {};

    return {
      teacherClassLogs,
      teacherClassroomId,
      teacherId,
      teacherReviewConfig: store.get(configs, TEACHER_PERFORMANCE_REVIEW),
    };
  },
  dispatch => ({
    actions: bindActionCreators(
      {
        closeModal,
        getClassLogs,
        getConfig,
        getTeacherClassStatus,
        invalidateReview,
        openModal,
        patchTeacherClassroom,
        patchTeacherReview,
        postTeacherReview,
      },
      dispatch,
    ),
  }),
)(TeacherClassroom);
